import { C } from '../data/constants.js';

class Particle {
  constructor() {
    this.active = false;
    this.x = 0; this.y = 0;
    this.vx = 0; this.vy = 0;
    this.life = 0; this.maxLife = 0;
    this.size = 0;
    this.color = '#fff';
    this.type = 'circle'; // 'circle' or 'rect'
    this.rotation = 0;
    this.rotationSpeed = 0;
    this.w = 0; this.h = 0; // for rect type
  }

  init(x, y, vx, vy, life, size, color, type = 'circle') {
    this.active = true;
    this.x = x; this.y = y;
    this.vx = vx; this.vy = vy;
    this.life = life; this.maxLife = life;
    this.size = size;
    this.color = color;
    this.type = type;
    this.rotation = Math.random() * Math.PI * 2;
    this.rotationSpeed = (Math.random() - 0.5) * 0.2;
    if (type === 'rect') {
      this.w = size * (0.5 + Math.random());
      this.h = size * (0.3 + Math.random() * 0.5);
    }
  }

  update() {
    if (!this.active) return;
    this.x += this.vx;
    this.y += this.vy;
    this.vy += C.PARTICLE_GRAVITY;
    this.vx *= C.PARTICLE_FRICTION;
    this.vy *= C.PARTICLE_FRICTION;
    this.rotation += this.rotationSpeed;
    this.life--;
    if (this.life <= 0) this.active = false;
  }

  render(ctx) {
    if (!this.active) return;
    const alpha = this.life / this.maxLife;
    ctx.globalAlpha = alpha;
    ctx.fillStyle = this.color;
    
    if (this.type === 'circle') {
      ctx.beginPath();
      ctx.arc(this.x, this.y, this.size * alpha, 0, Math.PI * 2);
      ctx.fill();
    } else {
      ctx.save();
      ctx.translate(this.x, this.y);
      ctx.rotate(this.rotation);
      ctx.fillRect(-this.w / 2, -this.h / 2, this.w, this.h);
      ctx.restore();
    }
    ctx.globalAlpha = 1;
  }
}

export class ParticleSystem {
  constructor() {
    this.pool = [];
    for (let i = 0; i < C.MAX_PARTICLES; i++) {
      this.pool.push(new Particle());
    }
  }

  _getParticle() {
    for (const p of this.pool) {
      if (!p.active) return p;
    }
    // Pool exhausted — recycle oldest
    return this.pool[0];
  }

  // Emit a burst of particles
  emit(x, y, count, color, opts = {}) {
    const {
      speedMin = 1, speedMax = 3,
      sizeMin = 1, sizeMax = 3,
      lifeMin = 15, lifeMax = 35,
      type = 'circle',
      directionBias = null  // { x, y } to bias direction
    } = opts;
    
    for (let i = 0; i < count; i++) {
      const p = this._getParticle();
      const angle = Math.random() * Math.PI * 2;
      const speed = speedMin + Math.random() * (speedMax - speedMin);
      let vx = Math.cos(angle) * speed;
      let vy = Math.sin(angle) * speed;
      
      if (directionBias) {
        vx += directionBias.x * 0.5;
        vy += directionBias.y * 0.5;
      }
      
      const size = sizeMin + Math.random() * (sizeMax - sizeMin);
      const life = lifeMin + Math.random() * (lifeMax - lifeMin);
      p.init(x, y, vx, vy, life, size, color, type);
    }
  }

  // Emit brick shatter effect: debris fragments + spark dots
  emitBrickShatter(x, y, w, h, color) {
    // Debris fragments (rectangular)
    this.emit(x + w/2, y + h/2, 6, color, {
      speedMin: 1.5, speedMax: 4,
      sizeMin: 3, sizeMax: 6,
      lifeMin: 20, lifeMax: 40,
      type: 'rect'
    });
    // Spark dots
    this.emit(x + w/2, y + h/2, 8, '#ffffff', {
      speedMin: 2, speedMax: 5,
      sizeMin: 1, sizeMax: 2,
      lifeMin: 10, lifeMax: 25,
      type: 'circle'
    });
  }

  update() {
    for (const p of this.pool) {
      p.update();
    }
  }

  render(ctx) {
    for (const p of this.pool) {
      p.render(ctx);
    }
  }

  get activeCount() {
    return this.pool.filter(p => p.active).length;
  }
}
